import { createFileRoute } from '@tanstack/react-router'
import { BrandedSpinner } from '@/components/BrandedSpinner'
import { useNavigate } from '@tanstack/react-router';
import { pollTransactionStatus } from '@/hooks/useStripe';
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useUser as useClerkUser } from '@clerk/clerk-react';
import { getUserFromClerkId } from '@/hooks/useUser';
import { userHasPaidAccess } from '@/lib/userSubscriptionHelpers';
import { trackEvent } from '@/lib/posthog';

// Safety valve: if access hasn't appeared this long after the transaction
// completes, send them in anyway rather than hang on this screen.
const ACCESS_WAIT_TIMEOUT_MS = 20000;

export const Route = createFileRoute('/_auth/checkout-success')({
  component: CheckoutSuccessComponent,
})

function AnimatedCheck() {
  return (
    <svg className="w-16 h-16" viewBox="0 0 52 52" fill="none">
      <circle
        cx="26"
        cy="26"
        r="24"
        stroke="#8b5cf6"
        strokeWidth="2.5"
        fill="#f5f3ff"
        className="animate-[scale-in_0.3s_ease-out]"
      />
      <path
        d="M15 27l7 7 15-15"
        stroke="#7c3aed"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        strokeDasharray="40"
        strokeDashoffset="40"
        className="animate-[draw-check_0.4s_0.3s_ease-out_forwards]"
      />
      <style>{`
        @keyframes draw-check {
          to { stroke-dashoffset: 0; }
        }
        @keyframes scale-in {
          from { transform: scale(0.8); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </svg>
  );
}

function CheckoutSuccessComponent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user: clerkUser } = useClerkUser();
  const hasRedirected = useRef(false);
  const hasTracked = useRef(false);

  let transactionId: string | undefined = undefined;
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    transactionId = params.get('transaction_id') || undefined;
  }

  const { data, isLoading, error } = pollTransactionStatus(transactionId || '');
  const txComplete = data?.status === 'COMPLETE';

  // Once the payment transaction is COMPLETE, poll /me until access actually
  // lands. The subscription row is created by a separate webhook
  // (customer.subscription.created) that can arrive AFTER the one that marks
  // the transaction COMPLETE (invoice.payment_succeeded). Each /me fetch runs
  // reconcileUserLicense server-side, so we just refetch until the user has
  // access, then send them in. Without this, a fast (e.g. card-free, 100%-off)
  // checkout returns before the row exists and parks the user on the paywall.
  const { data: userData } = getUserFromClerkId(clerkUser?.id ?? '', {
    enabled: txComplete && !!clerkUser?.id,
    refetchInterval: (query: any) =>
      userHasPaidAccess(query?.state?.data) ? false : 1500,
  });
  const hasAccess = userHasPaidAccess(userData);

  // Fire the conversion event once, as soon as payment is confirmed.
  useEffect(() => {
    if (txComplete && !hasTracked.current) {
      hasTracked.current = true;
      trackEvent('subscription_activated', { transaction_id: transactionId });
    }
  }, [txComplete, transactionId]);

  // Redirect once access is confirmed; fall back after a timeout so we never hang.
  useEffect(() => {
    if (!txComplete || hasRedirected.current) return;

    const go = () => {
      if (hasRedirected.current) return;
      hasRedirected.current = true;
      queryClient.invalidateQueries({ queryKey: ['user'] });
      navigate({ to: '/', search: { invitationcode: undefined, __clerk_ticket: undefined } });
    };

    if (hasAccess) {
      const timer = setTimeout(go, 1200); // brief beat so the check animation reads
      return () => clearTimeout(timer);
    }

    const fallback = setTimeout(go, ACCESS_WAIT_TIMEOUT_MS);
    return () => clearTimeout(fallback);
  }, [txComplete, hasAccess, navigate, queryClient]);

  if (!transactionId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-4">
        <p className="text-base text-red-600">Missing transaction ID.</p>
      </div>
    );
  }

  if (isLoading || data?.status === 'PENDING') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-4">
        <div className="text-center space-y-4">
          <BrandedSpinner size="xl" />
          <p className="text-base text-slate-500">
            {isLoading ? 'Checking payment status...' : 'Confirming your payment...'}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-4">
        <div className="text-center space-y-3 max-w-sm">
          <p className="text-base text-red-600">Something went wrong checking your payment.</p>
          <p className="text-sm text-slate-500">{error.message}</p>
        </div>
      </div>
    );
  }

  if (data?.status === 'COMPLETE') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-4">
        <div className="text-center space-y-5 max-w-sm mx-auto">
          <div className="flex justify-center">
            <AnimatedCheck />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold text-slate-900">You're all set</h1>
            <p className="text-base text-slate-500">
              {hasAccess ? 'Taking you in...' : 'Setting up your access...'}
            </p>
          </div>
          {hasAccess && (
            <button
              type="button"
              onClick={() => {
                if (hasRedirected.current) return;
                hasRedirected.current = true;
                queryClient.invalidateQueries({ queryKey: ['user'] });
                navigate({ to: '/', search: { invitationcode: undefined, __clerk_ticket: undefined } });
              }}
              className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-violet-600 hover:text-violet-700 hover:bg-violet-50 rounded-lg transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
            >
              Continue now
              <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3.5 8h9M8.5 4l4 4-4 4" />
              </svg>
            </button>
          )}
        </div>
      </div>
    );
  }

  if (data?.status === 'FAILED') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-4">
        <div className="text-center space-y-5 max-w-sm mx-auto">
          <div className="flex justify-center">
            <svg className="w-14 h-14" viewBox="0 0 52 52" fill="none">
              <circle cx="26" cy="26" r="24" stroke="#ef4444" strokeWidth="2.5" fill="#fef2f2" />
              <path d="M18 18l16 16M34 18L18 34" stroke="#dc2626" strokeWidth="3" strokeLinecap="round" />
            </svg>
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold text-slate-900">Payment didn't go through</h1>
            <p className="text-base text-slate-500">There was a problem processing your payment. Please try again or contact support.</p>
          </div>
          <button
            type="button"
            onClick={() => navigate({ to: '/subscribe', search: {} as any })}
            className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-lg transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return null;
}
